import ClientIntlProvider from '@gravity/client-intl-provider';
import * as NucleoManager from '@gravity/nucleo-manager';
import { evolutionTheme } from '@gravity/theme-mapper';
import { ThemeSelector, TicketShopTheme } from '@gravity/theme-provider';
import calendar from '@nucleo/calendar';
import cart from '@nucleo/cart';
import catalog from '@nucleo/catalog';
import config from '@nucleo/config';
import filter from '@nucleo/filter';
import productList from '@nucleo/product-list';
import resaleAvailability from '@nucleo/resale-availability';
import streamOnline from '@nucleo/stream-online';
import merge from 'lodash/merge';
import React from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import UsageTracker from "../src/UsageTracker";
import { getTranslationMessages } from './i18n';
import renderTo from './renderer';
import theme from './theme';
import WidgetContainer from './WidgetContainer';

const labelOverrides = window.secutix.__i18n__;

const getThemeResponsiveConfig = (themeName) => {
  switch (themeName) { // NOSONAR
    case TicketShopTheme.EVOLUTION: {
      const { breakpoints, screens, spacings } = evolutionTheme;
      return screens.reduce((result, screen) => {
        result.breakpoints.push(`${breakpoints[screen]}px`);
        result.space.push(spacings[screen]);
        return result;
      }, { breakpoints: [], space: [] });
    }
    default:
      return null;
  }
}

const render = (root, props, language, translations = {}, curTheme = {}, actions, store) => {
  let currentLanguage = language;

  const renderInitial = actualLanguage => {
    renderTo(
      <Provider store={store}>
        <IntlProvider locale={actualLanguage} messages={translations[actualLanguage]}>
          <ThemeProvider theme={{ ...curTheme, ...getThemeResponsiveConfig(curTheme.theme) }}>
            <ThemeSelector theme={curTheme.theme} devMode={translations[currentLanguage]?.['gravity.ts15.devMode']}>
              <WidgetContainer {...{ ...props, language: actualLanguage, actions, translations }} defaultTheme={curTheme.theme} />
            </ThemeSelector>
          </ThemeProvider>
        </IntlProvider>
      </Provider>,
      document.querySelector(root),
    );
  };
  store.subscribe(() => {
    const newLanguage = (store.getState().config && store.getState().config.language) || null;
    if (newLanguage && newLanguage !== currentLanguage) {
      currentLanguage = newLanguage;
      renderInitial(newLanguage);
    }
  });
  renderInitial(language);
};

const getNucleo = () =>
  NucleoManager.getNucleo(
    {
      catalog,
      cart,
      productList,
      resaleAvailability,
      filter,
      filterCalendar: filter,
      calendar,
      streamOnline,
    },
    { config },
  );
// The global initialization, it represents the name of the global to init (to avoid doing it more than one time in the wrapper)
const getGlobalInit = (widgetDef) => ({
  config: async(params, store, nucleo) => {
    // If we pass config like input of widget, the page not make a separate call to fetch the configuration
    const configPredefined = widgetDef.config;
    const { hostname, apiKey, language, restHost } = params;
    const { widgetBaseURL, widget, name } = widgetDef;
    const widgetName = widget || name;
    store.dispatch(nucleo.actions.config.init({ hostname, apiKey, language, restHost, widgetBaseURL, widgetName }));
    store.dispatch(
      nucleo.actions.default.filterCalendar.apply({
        filterCriteria: {
          topicIds: widgetDef.topicIds,
          spaceIds: widgetDef.spaceIds,
        },
      }),
    );

    await store.dispatch(nucleo.actions.config.load(configPredefined));
    const cfg = nucleo.actions.config.getLocalState(store.getState());
    await ClientIntlProvider(cfg.language);
  },
});

const Catalog = {
  getUsageTrackerConfig: () => UsageTracker.DEFAULT_USAGE_TRACKER_CONFIG,
  getNucleo,
  getGlobalInit,
  render: async (params, globalStore, nucleo = getNucleo()) => {
    const { hostname, apiKey, language, name, root, ...props } = params;
    let cfg = null;
    let store = null;
    if (globalStore) {
      store = globalStore;
      cfg = nucleo.actions.config.getLocalState(store.getState());
    } else {
      store = NucleoManager.createNucleoStore(nucleo);
      await getGlobalInit().config(store, nucleo, params);
      cfg = nucleo.actions.config.getLocalState(store.getState());
    }
    const translationMessages = await getTranslationMessages(cfg.language, labelOverrides);
    const translations = merge(
      translationMessages,
      cfg.labelOverrides && cfg.labelOverrides.currentTranslations,
    );
    render(
      root,
      props,
      cfg.language,
      translations,
      theme((cfg.style && cfg.style.properties) || {}),
      nucleo.actions,
      store,
    );
  },
};

export default Catalog;
